import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSEO } from '../hooks/useSEO';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, FileUser, ArrowRight, CheckCircle, Shield, Zap, Globe } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import MobileBottomNav from '../components/MobileBottomNav';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import ToolSidebar from '../components/ToolSidebar';
import SeoMainImage from '../components/SeoMainImage';
import HreflangTags from '../components/HreflangTags';
import '../styles/StampMakerPage.css';
import '../styles/HomePage.css';

const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";

const ResumePage = () => {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { theme } = useTheme();
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const seoDefaults = {
    title: 'Free Online Resume Builder — Create a Professional CV in Minutes',
    description: 'Build a professional resume or CV online for free. Choose modern templates and download as PDF.',
    keywords: 'resume builder, free CV maker, online resume, PDF resume, professional CV'
  };
  const seo = useSEO('resume', seoDefaults);

  return (
    <div className="stampmaker-page" data-testid="resume-page">
      <Helmet>
        <title>{seo?.title || seoDefaults.title}</title>
        <meta name="description" content={seo?.description || seoDefaults.description} />
        <meta name="keywords" content={seo?.keywords || seoDefaults.keywords} />
      </Helmet>

      <header className="dark-header">
        <div className="header-left">
          <div className="header-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src={currentLogo} alt="Registan Tourism" className="logo-img" />
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <button className="btn-user-profile" onClick={() => navigate('/dashboard')}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout">
              <LogOut size={20} />
            </button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => navigate('/login')}>{t('login') || 'Login'}</button>
            <button className="btn-primary btn-signup" onClick={() => navigate('/register')}>{t('signUp') || 'SIGN UP'}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)}>
          <Menu size={24} />
        </button>
      </header>
      <SeoMainImage seo={seo} />
      <HreflangTags path="/resume" />
      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="stampmaker-main tool-page-content">

          {/* Hero Section */}
          <div style={{ textAlign: 'center', padding: '50px 20px 30px', maxWidth: '800px', margin: '0 auto' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 18px', background: 'rgba(94,252,232,0.08)', border: '1px solid rgba(94,252,232,0.2)', borderRadius: '24px', marginBottom: '24px', fontSize: '13px', fontWeight: 700, color: '#5EFCE8', letterSpacing: '0.5px' }}>
              <Zap size={14} /> {t('rbFreeForever') || 'FREE FOREVER'}
            </div>
            <h1 data-testid="resume-h1" style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 900, lineHeight: 1.15, color: 'var(--text-primary)', margin: '0 0 16px', letterSpacing: '-1px' }}>
              {t('resumePageH1') || 'Professional Resume Builder — Land Your Dream Job in the UAE & Worldwide'}
            </h1>
            <h2 data-testid="resume-h2" style={{ fontSize: 'clamp(15px, 2vw, 18px)', fontWeight: 500, lineHeight: 1.5, color: 'var(--text-secondary)', margin: '0 0 32px' }}>
              {t('resumePageH2') || 'Craft a Stunning CV That Gets You Hired — Free, Fast & Ready to Download as PDF'}
            </h2>

            <button
              onClick={() => navigate('/resume/editor')}
              data-testid="resume-create-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '12px',
                padding: '18px 44px', fontSize: '17px', fontWeight: 800,
                background: 'linear-gradient(135deg, #5EFCE8, #60A5FA)',
                color: '#0a1628', border: 'none', borderRadius: '14px',
                cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
                boxShadow: '0 6px 30px rgba(94,252,232,0.25)',
                letterSpacing: '-0.3px'
              }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 40px rgba(94,252,232,0.35)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 6px 30px rgba(94,252,232,0.25)'; }}
            >
              <FileUser size={22} />
              {t('rbCreateResume') || 'Create My Resume'}
              <ArrowRight size={20} />
            </button>
          </div>

          {/* Trust Badges */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '32px', flexWrap: 'wrap', margin: '10px 0 40px', padding: '0 20px' }}>
            {[
              { icon: Shield, text: t('rbBadge1') || 'No Registration Required' },
              { icon: CheckCircle, text: t('rbBadge2') || 'ATS-Friendly Format' },
              { icon: Globe, text: t('rbBadge3') || 'Used Worldwide' },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 }}>
                <b.icon size={16} style={{ color: '#5EFCE8' }} /> {b.text}
              </div>
            ))}
          </div>

          {/* Divider */}
          <div style={{ width: '60px', height: '3px', background: 'linear-gradient(90deg, #5EFCE8, #60A5FA)', margin: '0 auto 40px', borderRadius: '2px' }} />

          {/* Description */}
          <div style={{ maxWidth: '750px', margin: '0 auto', padding: '0 20px 50px' }}>
            <p style={{ fontSize: '15px', lineHeight: 1.85, color: 'var(--text-secondary)', textAlign: 'center', margin: 0, fontWeight: 400 }}>
              {t('resumePageText') || 'Your resume is the first impression you make on any employer — make it count.'}
            </p>
          </div>

        </main>
      </div>

      <SiteFooter />
      <MobileBottomNav />
    </div>
  );
};

export default ResumePage;
